/**
 * URL parsing utilities
 */

import type { ExternalUpload } from "@design-lab/shared/types";

/**
 * Checks if the current URL is in edit mode.
 * @returns True if the current URL is in edit mode, false otherwise.
 */
export function isEditMode() {
  const searchParams = new URLSearchParams(window.location.search);
  const asgid = searchParams.get("asgid");
  const editParam = searchParams.get("edit");
  return editParam === "true" && !!asgid;
}

/**
 * Parses external uploads from URL parameter
 * Handles both JSON and comma-separated formats
 */
export function parseExternalUploads(urlsParam: string | undefined): ExternalUpload[] | undefined {
  if (!urlsParam) return undefined;

  const uploads: ExternalUpload[] = [];

  // First try to parse as JSON (for artwork_urls which can be JSON object)
  try {
    const parsed = JSON.parse(urlsParam);
    // Handle both array and object formats
    if (Array.isArray(parsed)) {
      parsed.forEach(url => {
        if (typeof url === "string") {
          uploads.push({ url });
        }
      });
    } else if (parsed && typeof parsed === "object") {
      Object.entries(parsed).forEach(([key, value]) => {
        if (typeof value === "string") {
          uploads.push({ id: key, url: value });
        }
      });
    }
  } catch {
    // If JSON parsing fails, try comma-separated format
    const parts = urlsParam
      .split(",")
      .map(part => part.trim())
      .filter(Boolean);
    parts.forEach(part => {
      try {
        uploads.push({ url: decodeURIComponent(part) });
      } catch {
        uploads.push({ url: part });
      }
    });
  }

  return uploads.length > 0 ? uploads : undefined;
}
